import { useCallback, useEffect, useState } from 'react';
import * as api from './api';
import './App.css';
import LoginPage from './components/LoginPage';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import DashboardPage from './pages/DashboardPage';
import VideosPage from './pages/VideosPage';
import AssignmentsPage from './pages/AssignmentsPage';
import QuizzesPage from './pages/QuizzesPage';

const pageTitles = {
  dashboard: 'Dashboard',
  employees: 'Employees',
  tracking: 'Progress Tracking',
  'employee-progress': 'Employee Progress',
  videos: 'Training',
  assignments: 'Assignments',
  quizzes: 'Quizzes',
};

export default function App() {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);
  const [page, setPage] = useState('dashboard');
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const openEmployeeProgress = (emp) => {
    setSelectedEmployee(emp);
    setPage('employee-progress');
  };

  const hydrate = useCallback(async () => {
    const token = localStorage.getItem('fh_token');
    if (!token) {
      setUser(null);
      setBooting(false);
      return;
    }
    try {
      const { data: me } = await api.getMe();
      setUser({ id: me.id, email: me.email, role: me.role });
    } catch {
      localStorage.removeItem('fh_token');
      setUser(null);
    } finally {
      setBooting(false);
    }
  }, []);

  useEffect(() => { hydrate(); }, [hydrate]);

  function onLogout() {
    localStorage.removeItem('fh_token');
    setUser(null);
    setPage('dashboard');
  }

  if (booting) {
    return <div className="login-wrap"><p style={{ color: 'white' }}>Loading...</p></div>;
  }

  if (!user) {
    return <LoginPage onSuccess={u => { setUser(u); setPage('dashboard'); }} />;
  }

  return (
    <div className="app">
      <Sidebar user={user} page={page} setPage={setPage} onLogout={onLogout} />
      <div className="main">
        <Topbar title={pageTitles[page] || 'Dashboard'} user={user} />
        <div className="content">
          {page === 'dashboard' && <DashboardPage user={user} />}
          {page === 'employees' && user.role === 'admin' && <EmployeesPage openEmployeeProgress={openEmployeeProgress} />}
          {page === 'tracking' && user.role === 'admin' && <TrackingPage openEmployeeProgress={openEmployeeProgress} />}
          {page === 'employee-progress' && user.role === 'admin' && (
            <EmployeeProgressPage employee={selectedEmployee} onBack={() => setPage('employees')} />
          )}
          {page === 'videos' && <VideosPage user={user} />}
          {page === 'assignments' && <AssignmentsPage user={user} />}
          {page === 'quizzes' && <QuizzesPage user={user} />}
        </div>
      </div>
    </div>
  );
}

// Placeholder components for admin-only pages (employees, tracking)
// These show data from the API in the polished design
function EmployeesPage({ openEmployeeProgress }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [formErr, setFormErr] = useState('');
  const [form, setForm] = useState({ name: '', email: '', department: 'Operations' });
  const [confirm, setConfirm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', department: 'Operations', status: 'active' });
  const [editErr, setEditErr] = useState('');

  const DEPARTMENTS = ['Operations', 'Compliance', 'Training', 'HR', 'IT', 'Finance'];

  const load = async () => {
    try {
      const { data } = await api.getEmployees();
      setEmployees(data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    setFormErr('');
    if (!form.name.trim()) { setFormErr('Name is required.'); return; }
    if (!form.email.trim()) { setFormErr('Email is required.'); return; }
    if (!/@(forthillassociates\.com|fhlogix\.com)$/i.test(form.email)) {
      setFormErr('Email must end with @forthillassociates.com or @fhlogix.com');
      return;
    }
    try {
      await api.createEmployee({
        name: form.name.trim(),
        email: form.email.trim(),
        department: form.department,
      });
      setForm({ name: '', email: '', department: 'Operations' });
      setModal(false);
      load();
    } catch (e) { setFormErr(e.response?.data?.error || 'Create failed'); }
  };

  const handleDelete = async (id) => {
    try { await api.deleteEmployee(id); setConfirm(null); load(); } catch (e) { console.error(e); }
  };

  const openEdit = (emp) => {
    setEditErr('');
    setEditForm({
      name: emp.name || '',
      department: emp.department || 'Operations',
      status: emp.status || 'active',
    });
    setEditing(emp);
  };

  const handleUpdate = async () => {
    setEditErr('');
    if (!editForm.name.trim()) { setEditErr('Name is required.'); return; }
    try {
      await api.updateEmployee(editing.id, {
        name: editForm.name.trim(),
        department: editForm.department,
        status: editForm.status,
      });
      setEditing(null);
      load();
    } catch (e) { setEditErr(e.response?.data?.error || 'Update failed'); }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div>
      {confirm && (
        <div className="modal-overlay" onClick={() => setConfirm(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="confirm-modal">
              <div className="confirm-icon">🗑️</div>
              <div className="confirm-title">Delete Employee</div>
              <div className="confirm-sub">Remove {confirm.name} from the portal? They will lose all access.</div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button className="btn btn-secondary" onClick={() => setConfirm(null)}>Cancel</button>
                <button className="btn btn-danger" onClick={() => handleDelete(confirm.id)}>Yes, Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="section-header">
        <div>
          <div className="section-title">Employee Management</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Only employees added here can log in to the portal</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setFormErr(''); setModal(true); }}>+ Add Employee</button>
      </div>
      <div className="card">
        {employees.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--muted)', fontSize: 13 }}>No employees added yet. Click "+ Add Employee" to get started.</div>
        ) : (
          <table>
            <thead>
              <tr><th>Employee</th><th>Email</th><th>Department</th><th>Status</th><th>Progress</th><th style={{ textAlign: 'right' }}>Actions</th></tr>
            </thead>
            <tbody>
              {employees.map(e => (
                <tr key={e.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className="avatar" style={{ width: 30, height: 30, fontSize: 11 }}>{e.avatar || (e.name || '').slice(0, 2).toUpperCase()}</div>
                      <div><div style={{ fontWeight: 500 }}>{e.name}</div><div style={{ fontSize: 11, color: 'var(--muted)' }}>{e.join_date}</div></div>
                    </div>
                  </td>
                  <td style={{ color: 'var(--muted)', fontSize: 12 }}>{e.email}</td>
                  <td><span className="tag">{e.department}</span></td>
                  <td><span className={`badge ${e.status === 'active' ? 'badge-green' : 'badge-gray'}`}>{e.status}</span></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className="progress-bar" style={{ width: 80 }}>
                        <div className="progress-fill" style={{ width: (e.overall_progress || 0) + '%' }}></div>
                      </div>
                      <span style={{ fontSize: 12 }}>{e.overall_progress || 0}%</span>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEmployeeProgress(e)}>View</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(e)}>Edit</button>
                      <button className="btn-icon" onClick={() => setConfirm({ id: e.id, name: e.name })}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">Add New Employee</span><button className="close-btn" onClick={() => setModal(false)}>×</button></div>
            <div className="notification" style={{ marginBottom: 16 }}>Employee will sign in with just their organization email — no password needed.</div>
            {formErr && <div className="alert">{formErr}</div>}
            <div className="form-group"><label className="form-label">Full Name *</label><input className="form-input" placeholder="John Smith" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div className="form-group">
              <label className="form-label">Organization Email *</label>
              <input className="form-input" type="email" placeholder="john@forthillassociates.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Must be @forthillassociates.com or @fhlogix.com</div>
            </div>
            <div className="form-group"><label className="form-label">Department</label>
              <select className="form-select" value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))}>
                {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdd}>Add Employee</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="modal-overlay" onClick={() => setEditing(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Edit Employee</span>
              <button className="close-btn" onClick={() => setEditing(null)}>×</button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>{editing.email}</div>
            {editErr && <div className="alert">{editErr}</div>}
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input className="form-input" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Department</label>
              <select className="form-select" value={editForm.department} onChange={e => setEditForm(f => ({ ...f, department: e.target.value }))}>
                {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleUpdate}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TrackingPage({ openEmployeeProgress }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.getEmployees();
        setEmployees(data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <p>Loading...</p>;

  return (
    <div>
      <div className="section-header">
        <div className="section-title">Progress Tracking</div>
      </div>
      <div className="grid-2">
        {employees.length === 0 && <div style={{ fontSize: 13, color: 'var(--muted)', padding: '20px 0' }}>No employees to track yet.</div>}
        {employees.map(e => (
          <div
            key={e.id}
            className="employee-card"
            onClick={() => openEmployeeProgress(e)}
            style={{ cursor: 'pointer' }}
          >
            <div className="avatar" style={{ width: 44, height: 44, fontSize: 14 }}>{e.avatar || (e.name || '').slice(0, 2).toUpperCase()}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>{e.name}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>{e.department}</div>
              <div className="progress-bar"><div className="progress-fill" style={{ width: (e.overall_progress || 0) + '%' }}></div></div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--teal)' }}>{e.overall_progress || 0}%</div>
              <span className={`badge ${e.status === 'active' ? 'badge-green' : 'badge-gray'}`}>{e.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmployeeProgressPage({ employee, onBack }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employee?.id) return;
    (async () => {
      try {
        const { data } = await api.getEmployeeProgress(employee.id);
        setRows(data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [employee?.id]);

  if (!employee) {
    return (
      <div>
        <button className="btn btn-secondary btn-sm" onClick={onBack}>← Back</button>
        <p style={{ marginTop: 12 }}>No employee selected.</p>
      </div>
    );
  }

  const totalVideos     = rows.length;
  const completedVideos = rows.filter(r => r.video_status === 'completed').length;
  const assignmentsDone = rows.filter(r => r.assignment_submitted).length;
  const quizzesPassed   = rows.filter(r => r.quiz_passed).length;
  const overall = totalVideos === 0 ? 0 : Math.round((completedVideos / totalVideos) * 100);

  return (
    <div>
      <div className="breadcrumb">
        <span className="breadcrumb-link" onClick={onBack}>← Employees</span>
        <span>/</span>
        <span>{employee.name}</span>
      </div>

      <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
        <div className="avatar" style={{ width: 56, height: 56, fontSize: 18 }}>
          {employee.avatar || (employee.name || '').slice(0, 2).toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{employee.name}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{employee.email}</div>
          <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
            <span className="tag">{employee.department}</span>
            <span className={`badge ${employee.status === 'active' ? 'badge-green' : 'badge-gray'}`}>{employee.status}</span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--teal)' }}>{overall}%</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>Overall progress</div>
        </div>
      </div>

      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="stat-card">
          <div className="stat-label">Videos Completed</div>
          <div className="stat-val">{completedVideos}<span style={{ fontSize: 14, color: 'var(--muted)', fontWeight: 500 }}> / {totalVideos}</span></div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Assignments Submitted</div>
          <div className="stat-val">{assignmentsDone}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Quizzes Passed</div>
          <div className="stat-val">{quizzesPassed}</div>
        </div>
      </div>

      <div className="section-header"><div className="section-title">Training Details</div></div>
      <div className="card">
        {loading ? (
          <p>Loading...</p>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--muted)', fontSize: 13 }}>
            No training assigned yet.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Video</th>
                <th>Folder</th>
                <th>Status</th>
                <th>Assignment</th>
                <th>Quiz</th>
                <th>Completed</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.video_id}>
                  <td style={{ fontWeight: 500 }}>{r.title}</td>
                  <td><span className="tag">{r.folder_name || '—'}</span></td>
                  <td>
                    <span className={`badge ${
                      r.video_status === 'completed' ? 'badge-green' :
                      r.video_status === 'in_progress' ? 'badge-amber' : 'badge-gray'
                    }`}>
                      {r.video_status.replace('_', ' ')}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${r.assignment_submitted ? 'badge-green' : 'badge-gray'}`}>
                      {r.assignment_submitted ? 'Submitted' : 'Pending'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${
                      r.quiz_passed === true ? 'badge-green' :
                      r.quiz_passed === false ? 'badge-red' : 'badge-gray'
                    }`}>
                      {r.quiz_passed === true ? 'Passed' : r.quiz_passed === false ? 'Failed' : 'Not attempted'}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {r.completed_at ? new Date(r.completed_at).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
